import { useSelector } from 'react-redux'
import useScript from 'react-script-hook'
import React, { useCallback } from 'react'

import { RootState } from '@Root/slices'
import { MEDIA_VIEWER_JS } from '@Root/contants/urlConstants'
import { MY_HOUSE_NOTICE_CATEGORY, MY_HOUSE_NOTICE_CATEGORY_OBJ } from '@Root/contants/myhouse'
import MyhouseArticleImageResponse from '@Types/entities/MyhouseArticleImageResponse'
import { THUMBNAIL } from '@Root/contants'

declare const MediaViewer: any

const initialMediaViewer = {
  open: () => {
    return
  },
  close: () => {
    return
  },
  mediaViewer: null,
}

interface OpenParameter {
  imageList: MyhouseArticleImageResponse[]
  desc: string
  svcName: string
  mediaViewer: any
}

export default function useMediaViewer() {
  const notice = useSelector((state: RootState) => state.notice)
  const review = useSelector((state: RootState) => state.review)

  const [loading, error] = useScript({
    src: `${process.env.REACT_APP_MEDIA_VIEWER_SOURCE}${MEDIA_VIEWER_JS.MOBILE}`,
  })

  const open = (param: OpenParameter) => (index: number) => {
    const { imageList, desc, svcName, mediaViewer } = param

    const imgList = imageList?.map(({ url }: { url: string }) => ({
      itemType: 'image',
      orgUrl: url,
      thumbUrl: `${url}?${THUMBNAIL.M562}`,
      caption: { desc, svcName },
    }))

    mediaViewer.removeData()
    mediaViewer.addData(imgList)
    mediaViewer.open(index)
  }

  const close = (mediaViewer: any) => () => {
    mediaViewer.close()
  }

  const useReviewMediaViewer = useCallback(() => {
    const { detail } = review
    const { imageList, content } = detail

    if (loading || !imageList?.length) {
      return initialMediaViewer
    }

    const mediaViewer = getOriginalMediaViewer()
    mediaViewer.addLayout(getOriginalLayout(mediaViewer))

    return {
      mediaViewer,
      open: open({ mediaViewer, imageList, desc: content, svcName: '입주민리뷰' }),
      close: close(mediaViewer),
    }
  }, [loading])

  const useNoticeMediaViewer = useCallback(() => {
    const { detail } = notice
    const { imageList, title, category } = detail

    if (loading || !imageList?.length) {
      return initialMediaViewer
    }
    const mediaViewer = getOriginalMediaViewer()
    mediaViewer.addLayout(getOriginalLayout(mediaViewer))

    return {
      mediaViewer,
      open: open({
        mediaViewer,
        imageList,
        desc: title,
        svcName: MY_HOUSE_NOTICE_CATEGORY_OBJ[category as MY_HOUSE_NOTICE_CATEGORY].LABEL,
      }),
      close: close(mediaViewer),
    }
  }, [loading])

  const getOriginalMediaViewer = () => {
    const mediaViewer = new MediaViewer.Viewer({
      elItemList: document.getElementById('root'),
      useImgDownload: false,
      urlCallback: function (index: number) {
        return [window.location.protocol, '//', window.location.host, window.location.pathname, window.location.search, '#', index].join('')
      },
    })

    return mediaViewer
  }

  const getAuthMediaViewer = () => {
    const mediaViewer = new MediaViewer.Viewer({
      elItemList: document.getElementById('root'),
      useImgDownload: false,
      urlCallback: function (index: number) {
        // urlCallback 미 정의시 미디어뷰어 오픈 -> 뒤로가기시 hash 제거로 처리를 강제로 한 경우 크롬 등에서 회색화면이 노출됩니다.
        // urlCallback 의 return을 줄 경우 mediaviewer 내부의 pushState와 replaceState를 제어할 수 없어 의도와 다른 동작 수행.
      },
    })

    mediaViewer.on({
      [MediaViewer.MediaViewerEvent.OPEN]: () => {
        if (!window.location.hash) {
          window.history.pushState(null, '', '#0')
        }
      },
      [MediaViewer.MediaViewerEvent.CLOSE]: () => {
        if (window.location.hash) {
          window.history.back()
        }
      },
    })

    return mediaViewer
  }

  const getOriginalLayout = (initialMediaViewer: any) => {
    return new MediaViewer.CustomLayout({
      id: 'openOriginal',
      position: MediaViewer.LayoutPosition.BUTTONS,
      initialize: (parentElement: any) => {
        const wrapperElement = document.createElement('span')
        const element = document.createElement('button')
        element.setAttribute('class', '_btn_original_view')
        element.style.position = 'absolute'
        element.style.top = '15px'
        element.style.right = '15px'
        element.style.fontSize = '16px'
        element.style.color = '#ffffff'
        element.style.zIndex = '2001'
        element.innerHTML = '원본보기<span class="blind">원본 이미지 보기</span>'
        wrapperElement.appendChild(element)
        parentElement.appendChild(wrapperElement)
        return wrapperElement
      },

      onClick: (event: React.SyntheticEvent) => {
        event.preventDefault()
        event.stopPropagation()

        window.open(initialMediaViewer.getCurrent().orgUrl, '_blank')
      },
    })
  }

  const useAuthMediaViewer = useCallback(
    (list) => {
      if (loading || !list?.length) {
        return initialMediaViewer
      }

      const mediaViewer = getAuthMediaViewer()
      mediaViewer.addLayout(getAuthLayout(mediaViewer))
      const open = (index: number) => {
        const imgList = list?.map(({ url, svcName, desc }: { url: string; svcName: string; desc: string }) => ({
          itemType: 'image',
          thumbUrl: `${url}`,
          caption: { desc, svcName },
        }))

        mediaViewer.removeData()
        mediaViewer.addData(imgList)
        mediaViewer.open(index)
      }

      return {
        mediaViewer,
        open: open,
        close: close(mediaViewer),
      }
    },
    [loading],
  )

  const getAuthLayout = (initialMediaViewer: any) => {
    return new MediaViewer.CustomLayout({
      id: 'openOriginal',
      position: MediaViewer.LayoutPosition.BUTTONS,
      initialize: (parentElement: any) => {
        const wrapperElement = document.createElement('span')
        return wrapperElement
      },

      onClick: (event: React.SyntheticEvent) => {
        event.preventDefault()
        event.stopPropagation()

        window.open(initialMediaViewer.getCurrent().orgUrl, '_blank')
      },
    })
  }

  return { loading, useNoticeMediaViewer, useReviewMediaViewer, useAuthMediaViewer }
}
